import { useState, useEffect, useCallback } from "react";
import HelpButton from "../components/HelpButton";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import CustomerDashboard from "./CustomerDashboard";
import PublicDocOpenedWidget from "../components/PublicDocOpenedWidget";
import HottestCustomersWidget from "../components/HottestCustomersWidget";
import WeeklyDigestWidget from "../components/WeeklyDigestWidget";
import { Package, ChartBar, Users, Megaphone, TrendUp, CurrencyDollar, Handshake, Bell, Flame, ListChecks, ClockClockwise, Warning, CalendarBlank, ChartLine } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from "recharts";
import { CHART_TOOLTIP_STYLE, CHART_TICK_SM, CHART_TICK_XS } from "../config/uiHelpers";

const Dashboard = ({ user, logout }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [crm, setCrm] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [followUps, setFollowUps] = useState(null);
  const [loading, setLoading] = useState(true);

  const isCustomer = user?.role === "customer";

  const fetchAll = useCallback(async () => {
    if (isCustomer) { setLoading(false); return; }
    try {
      await axios.post(`${API}/seed-data?requester_role=${user?.role || ""}`).catch(() => {});
      const fuParams = new URLSearchParams();
      if (user?.role) fuParams.set("requester_role", user.role);
      if (user?.name) fuParams.set("requester_name", user.name);
      const [aRes, cRes, fRes, fnRes, nRes, fuRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/crm/dashboard`),
        axios.get(`${API}/crm/forecasting`).catch(() => ({ data: null })),
        axios.get(`${API}/crm/sales-funnel`).catch(() => ({ data: null })),
        axios.get(`${API}/crm/notifications/${user?.user_id || 'all'}`).catch(() => ({ data: [] })),
        api.get(`${API}/leads/follow-ups/dashboard?${fuParams.toString()}`).catch(() => ({ data: null })),
      ]);
      setAnalytics(aRes.data);
      setCrm(cRes.data);
      setForecast(fRes.data);
      setFunnel(fnRes.data);
      setNotifications(nRes.data);
      setFollowUps(fuRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [user?.user_id, user?.role, user?.name, isCustomer]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (isCustomer) {
    return <CustomerDashboard user={user} logout={logout} />;
  }

  const COLORS = ['#002FA7', '#10B981', '#F59E0B', '#DC2626', '#8B5CF6'];

  const topStats = [
    { label: "Products", value: analytics?.total_products || 0, icon: Package, color: "#002FA7", path: "/products" },
    { label: "Active Campaigns", value: analytics?.active_campaigns || 0, icon: Megaphone, color: "#10B981", path: "/campaigns" },
    { label: "Total Leads", value: analytics?.total_leads || 0, icon: Users, color: "#F59E0B", path: "/leads" },
    { label: "Pipeline Value", value: `₹${(forecast?.total_pipeline || 0).toLocaleString('en-IN')}`, icon: CurrencyDollar, color: "#DC2626", path: "/forecasting" },
    { label: "Win Rate", value: `${forecast?.win_rate || 0}%`, icon: TrendUp, color: "#8B5CF6", path: "/forecasting" },
    { label: "Contacts", value: crm?.total_contacts || 0, icon: Handshake, color: "#002FA7", path: "/crm/contacts" }
  ];

  const pipelineData = crm?.pipeline ? [
    { name: "Prospect", value: crm.pipeline.prospect?.value || 0 },
    { name: "Negotiation", value: crm.pipeline.negotiation?.value || 0 },
    { name: "Proposal", value: crm.pipeline.proposal?.value || 0 },
    { name: "Won", value: crm.pipeline.closed_won?.value || 0 },
  ] : [];

  const funnelData = funnel?.funnel?.filter(f => f.stage !== 'closed_lost').map(f => ({
    name: f.stage.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()),
    count: f.count,
    value: f.value
  })) || [];

  const unread = notifications.filter(n => !n.read).length;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="page-header-sticky">
          <div className="px-8 py-5 flex justify-between items-start">
            <div>
              <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
                Dashboard
              </h1><HelpButton moduleKey="dashboard" /></div>
              <p className="text-base text-slate-700 mt-1">Welcome back, <span className="font-semibold">{user?.name}</span></p>
            </div>
            {unread > 0 && (
              <div data-testid="notification-badge" className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-none px-4 py-2">
                <Bell size={20} weight="fill" className="text-red-500" />
                <span className="text-sm font-bold text-red-700">{unread} new</span>
              </div>
            )}
          </div>
        </div>
        <div className="p-8 pt-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="loading-spinner"></div></div>
          ) : (
            <>
              {/* KPI Row */}
              <div data-testid="kpi-row" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                {topStats.map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} onClick={() => navigate(stat.path)} data-testid={`kpi-${stat.label.toLowerCase().replace(' ', '-')}`}
                      className="bg-white border border-slate-200 rounded-none shadow-sm p-5 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all duration-300 group">
                      <Icon size={24} weight="duotone" style={{color: stat.color}} className="mb-3 group-hover:scale-110 transition-transform duration-300" />
                      <div className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: stat.color}}>{stat.value}</div>
                      <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Phase 4b-2: Public PDF Link Opened notifications */}
              <PublicDocOpenedWidget user={user} />

              {/* Phase 4b-3 add-on: Weekly digest summary (Mon morning 5-second pulse) */}
              <WeeklyDigestWidget user={user} />

              {/* Phase 4b-3: Hottest Customers (last 7 days, real public-PDF-open data) */}
              <div className="mb-8">
                <HottestCustomersWidget user={user} days={7} limit={5} />
              </div>

              {/* Today's Follow-ups widget (Phase 2 enhancement) */}
              {followUps && (
                <div data-testid="followups-widget" className="bg-white border border-slate-200 rounded-none shadow-sm mb-8 overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center gap-2">
                      <ClockClockwise size={16} weight="bold" className="text-[#002FA7]" />
                      <h3 className="text-sm uppercase tracking-[0.2em] font-bold text-slate-700">
                        {user?.role === "bde" ? "My Follow-ups" : "Team Follow-ups"}
                      </h3>
                    </div>
                    <button
                      data-testid="followups-widget-cta"
                      onClick={() => navigate("/leads")}
                      className="text-xs uppercase tracking-wider text-[#002FA7] hover:text-[#00227A] font-bold"
                    >
                      Open Leads →
                    </button>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
                    <button
                      data-testid="followups-due-today"
                      onClick={() => navigate("/leads")}
                      className="px-5 py-4 text-left hover:bg-amber-50 transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarBlank size={14} className="text-amber-600" />
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Due Today</span>
                      </div>
                      <div className="text-3xl font-bold text-amber-700 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                        {followUps.due_today ?? 0}
                      </div>
                    </button>
                    <button
                      data-testid="followups-overdue"
                      onClick={() => navigate("/leads")}
                      className={`px-5 py-4 text-left transition-colors group ${(followUps.overdue ?? 0) > 0 ? "bg-rose-50/50 hover:bg-rose-50" : "hover:bg-slate-50"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Warning size={14} weight={(followUps.overdue ?? 0) > 0 ? "fill" : "regular"} className={(followUps.overdue ?? 0) > 0 ? "text-rose-600" : "text-slate-400"} />
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Overdue</span>
                      </div>
                      <div className={`text-3xl font-bold tabular-nums ${(followUps.overdue ?? 0) > 0 ? "text-rose-700" : "text-slate-700"}`} style={{fontFamily: 'Outfit, sans-serif'}}>
                        {followUps.overdue ?? 0}
                      </div>
                    </button>
                    <button
                      data-testid="followups-next-7"
                      onClick={() => navigate("/leads")}
                      className="px-5 py-4 text-left hover:bg-blue-50 transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <ChartLine size={14} className="text-blue-600" />
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Next 7 Days</span>
                      </div>
                      <div className="text-3xl font-bold text-blue-700 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                        {followUps.next_7_days ?? 0}
                      </div>
                    </button>
                    <button
                      data-testid="followups-total"
                      onClick={() => navigate("/leads")}
                      className="px-5 py-4 text-left hover:bg-slate-50 transition-colors group"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <ClockClockwise size={14} className="text-slate-500" />
                        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total Logged</span>
                      </div>
                      <div className="text-3xl font-bold text-slate-700 tabular-nums" style={{fontFamily: 'Outfit, sans-serif'}}>
                        {followUps.total_logged ?? 0}
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Revenue Forecast */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Revenue Forecast</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={forecast?.monthly_forecast || []}>
                      <defs>
                        <linearGradient id="colorProj" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#002FA7" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#002FA7" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="month" stroke="#475569" tick={CHART_TICK_XS} />
                      <YAxis stroke="#475569" tick={CHART_TICK_XS} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, 'Projected']} />
                      <Area type="monotone" dataKey="projected" stroke="#002FA7" strokeWidth={2} fillOpacity={1} fill="url(#colorProj)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Pipeline Breakdown */}
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Pipeline</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pipelineData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                        {pipelineData.map((_, i) => <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={(v) => [`₹${v.toLocaleString('en-IN')}`]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {pipelineData.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2">
                        <div className="w-2 h-2" style={{backgroundColor: COLORS[i]}}></div>
                        <span className="text-xs text-slate-600">{d.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Funnel */}
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Sales Funnel</h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={funnelData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis type="number" stroke="#475569" tick={CHART_TICK_SM} />
                      <YAxis type="category" dataKey="name" stroke="#475569" tick={CHART_TICK_SM} width={80} />
                      <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#002FA7" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Tasks + Performance */}
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Task Overview</h3>
                  <div className="space-y-4">
                    {[
                      { label: "Pending", value: crm?.pending_tasks || 0, color: "#F59E0B" },
                      { label: "In Progress", value: crm?.in_progress_tasks || 0, color: "#002FA7" },
                      { label: "Completed", value: crm?.completed_tasks || 0, color: "#10B981" },
                    ].map(t => (
                      <div key={t.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-slate-600">{t.label}</span>
                          <span className="text-sm font-bold" style={{color: t.color}}>{t.value}</span>
                        </div>
                        <div className="w-full bg-slate-100 h-2">
                          <div className="h-2" style={{backgroundColor: t.color, width: `${crm?.total_tasks ? (t.value / crm.total_tasks * 100) : 0}%`}}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-200 flex justify-between text-sm">
                    <span className="text-slate-600">Conversion Rate</span>
                    <span className="font-bold text-[#002FA7]">{analytics?.conversion_rate || 0}%</span>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
                  <h3 className="text-lg font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Quick Actions</h3>
                  <div className="space-y-2">
                    {[
                      { label: "New Campaign", path: "/campaigns", icon: Megaphone },
                      { label: "Add Contact", path: "/crm/contacts", icon: Users },
                      { label: "View Deals", path: "/crm/deals", icon: Handshake },
                      { label: "Lead Scoring", path: "/lead-scoring", icon: Flame },
                      { label: "Team Performance", path: "/team-performance", icon: TrendUp },
                      { label: "View Tasks", path: "/crm/tasks", icon: ListChecks },
                    ].map(a => {
                      const Icon = a.icon;
                      return (
                        <button key={a.label} data-testid={`quick-${a.label.toLowerCase().replace(' ', '-')}`}
                          onClick={() => navigate(a.path)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-none bg-slate-50 hover:bg-slate-100 border border-slate-200 text-left transition-colors duration-300 group">
                          <Icon size={16} weight="duotone" className="text-[#002FA7] group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-sm font-medium text-slate-700">{a.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
